import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../api';
import TempPasswordNotice from './TempPasswordNotice';

const ROLE_LABELS = { admin: 'Admin', designer: 'Designer', requester: 'Requester' };

// Account management (admin). Accounts are created by hand; the temporary password is shown ONCE
// and the user must change it at first login.
export default function UsersSection({ loggedInUser }) {
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ username: '', role: 'requester', person_id: '' });
  const [error, setError] = useState('');
  const [issued, setIssued] = useState(null); // { username, temporaryPassword }
  const [roleDraft, setRoleDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/users`),
        axios.get(`${API_BASE_URL}/api/people`, { params: { active: 1 } }),
      ]);
      setUsers(u.data); setPeople(p.data); setRoleDraft({});
    } catch { toast.error('Could not load users.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (fn) => {
    setError('');
    try { return await fn(); } catch (err) {
      setError(err.response?.data?.error || 'Action failed.');
      return null;
    }
  };

  const create = async () => {
    const res = await call(() => axios.post(`${API_BASE_URL}/api/users`, {
      username: form.username, role: form.role, person_id: form.role === 'requester' ? Number(form.person_id) || null : null,
    }));
    if (res) {
      setIssued({ username: res.data.username, temporaryPassword: res.data.temporaryPassword });
      setForm({ username: '', role: 'requester', person_id: '' });
      load();
    }
  };

  const saveRole = async (u) => {
    const draft = roleDraft[u.id] || {};
    const role = draft.role || u.role;
    const person = draft.person_id !== undefined ? draft.person_id : u.person_id;
    if (await call(() => axios.put(`${API_BASE_URL}/api/users/${u.id}/role`, { role, person_id: role === 'requester' ? Number(person) || null : null }))) {
      toast.success('Role updated.');
      load();
    }
  };

  const reset = async (u) => {
    if (!window.confirm(`Reset the password for ${u.username}? The old password stops working.`)) return;
    const res = await call(() => axios.post(`${API_BASE_URL}/api/users/${u.id}/reset-password`));
    if (res) { setIssued({ username: res.data.username, temporaryPassword: res.data.temporaryPassword }); load(); }
  };

  const input = 'border p-2 rounded text-black bg-white dark:bg-black dark:text-white';
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2 max-w-3xl">
        <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
          placeholder="Username" className={`${input} flex-1`} />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input} aria-label="Role">
          {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {form.role === 'requester' && (
          <select value={form.person_id} onChange={(e) => setForm({ ...form, person_id: e.target.value })} className={input} aria-label="Person">
            <option value="">Person (Requested by)</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <button onClick={create} className="bg-indigo-500 text-white px-4 py-2 rounded">Create account</button>
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">{error}</p>}
      <TempPasswordNotice issued={issued} onClose={() => setIssued(null)} />
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="border p-2 text-left">User</th>
            <th className="border p-2 text-left">Role</th>
            <th className="border p-2 text-left">Person</th>
            <th className="border p-2" />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const self = u.username === loggedInUser;
            const role = (roleDraft[u.id] && roleDraft[u.id].role) || u.role;
            const person = roleDraft[u.id] && roleDraft[u.id].person_id !== undefined ? roleDraft[u.id].person_id : u.person_id;
            return (
              <tr key={u.id}>
                <td className="border p-2">{u.username}{u.must_change_password ? <em className="ml-2 text-xs text-yellow-700">(awaiting password change)</em> : null}</td>
                <td className="border p-2">
                  <select disabled={self} value={role} aria-label={`Role ${u.username}`}
                    onChange={(e) => setRoleDraft({ ...roleDraft, [u.id]: { ...roleDraft[u.id], role: e.target.value } })}
                    className={`${input} p-1`}>
                    {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="border p-2">
                  {role === 'requester' ? (
                    <select value={person || ''} aria-label={`Person ${u.username}`}
                      onChange={(e) => setRoleDraft({ ...roleDraft, [u.id]: { ...roleDraft[u.id], person_id: e.target.value } })}
                      className={`${input} p-1`}>
                      <option value="">—</option>
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : '—'}
                </td>
                <td className="border p-2 whitespace-nowrap">
                  <button disabled={self || !roleDraft[u.id]} onClick={() => saveRole(u)}
                    className="bg-indigo-600 text-white px-2 py-1 rounded mr-1 disabled:opacity-40">Save</button>
                  <button disabled={self} onClick={() => reset(u)}
                    className="bg-gray-600 text-white px-2 py-1 rounded disabled:opacity-40">Reset password</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
